const configuredBackendUrl = import.meta.env.VITE_ORCA_BACKEND_URL?.trim();
const DEFAULT_ORCA_BACKEND_URL = import.meta.env.DEV
  ? ""
  : "https://api.orcaagent.co";

const isTemporaryBackendUrl = (value: string) =>
  /(^|\.)ngrok(-free)?\./i.test(value) ||
  /(^|\.)ngrok\.io/i.test(value) ||
  /\.ngrok\.app/i.test(value) ||
  /\.ngrok-free\.app/i.test(value);

export const normalizeBackendUrl = (value?: string | null) => {
  if (!value) return "";
  const withProtocol =
    value.startsWith("http://") || value.startsWith("https://")
      ? value
      : `https://${value}`;
  return withProtocol.replace(/\/+$/, "");
};

export const getBackendBaseUrl = (fallbackUrl?: string | null) => {
  const backendBaseUrl = normalizeBackendUrl(
    configuredBackendUrl || DEFAULT_ORCA_BACKEND_URL || fallbackUrl
  );

  if (backendBaseUrl && isTemporaryBackendUrl(backendBaseUrl)) {
    throw new Error(
      "ORCA backend is still pointed at a temporary tunnel URL. Set VITE_ORCA_BACKEND_URL to the stable backend."
    );
  }

  return backendBaseUrl;
};

export const setConversationActive = async ({
  conversationId,
  active,
  accessToken,
  fallbackBackendUrl,
}: {
  conversationId: string;
  active: boolean;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) => {
  const backendBaseUrl = getBackendBaseUrl(fallbackBackendUrl);

  if (!backendBaseUrl) {
    throw new Error("Backend URL is not configured for this dashboard.");
  }

  const response = await fetch(`${backendBaseUrl}/api/conversation-active`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      active,
    }),
  });

  const result = await response.json().catch(() => null);

  if (!response.ok || result?.status !== "success") {
    throw new Error(
      result?.message || `Failed to update agent status: ${response.status}`
    );
  }

  return result.conversation;
};

const postConversationAction = async ({
  path,
  conversationId,
  accessToken,
  fallbackBackendUrl,
}: {
  path: string;
  conversationId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) => {
  const backendBaseUrl = getBackendBaseUrl(fallbackBackendUrl);

  if (!backendBaseUrl) {
    throw new Error("Backend URL is not configured for this dashboard.");
  }

  const response = await fetch(`${backendBaseUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
    body: JSON.stringify({
      conversation_id: conversationId,
    }),
  });

  const result = await response.json().catch(() => null);

  if (!response.ok || result?.status !== "success") {
    throw new Error(
      result?.message || result?.detail || `Conversation action failed: ${response.status}`
    );
  }

  return result;
};

export const archiveConversation = async ({
  conversationId,
  accessToken,
  fallbackBackendUrl,
}: {
  conversationId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postConversationAction({
    path: "/api/conversations/archive",
    conversationId,
    accessToken,
    fallbackBackendUrl,
  });

export const deleteConversation = async ({
  conversationId,
  accessToken,
  fallbackBackendUrl,
}: {
  conversationId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postConversationAction({
    path: "/api/conversations/delete",
    conversationId,
    accessToken,
    fallbackBackendUrl,
  });

export const submitBrokerInput = async ({
  conversationId,
  input,
  accessToken,
  fallbackBackendUrl,
}: {
  conversationId: string;
  input: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) => {
  const backendBaseUrl = getBackendBaseUrl(fallbackBackendUrl);

  if (!backendBaseUrl) {
    throw new Error("Backend URL is not configured for this dashboard.");
  }

  const response = await fetch(`${backendBaseUrl}/api/broker-input`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      input,
    }),
  });

  const result = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      result?.message || result?.detail || `Failed to submit broker input: ${response.status}`
    );
  }

  return result;
};

const postProposalAction = async ({
  path,
  proposalId,
  accessToken,
  fallbackBackendUrl,
}: {
  path: string;
  proposalId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) => {
  const backendBaseUrl = getBackendBaseUrl(fallbackBackendUrl);

  if (!backendBaseUrl) {
    throw new Error("Backend URL is not configured for this dashboard.");
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 60000);

  let response: Response;
  try {
    response = await fetch(`${backendBaseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
      },
      body: JSON.stringify({
        proposal_id: proposalId,
      }),
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("Proposal action timed out. Check the backend runtime and try again.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  const result = await response.json().catch(() => null);

  if (!response.ok || result?.status !== "success") {
    throw new Error(
      result?.message || result?.detail || `Proposal action failed: ${response.status}`
    );
  }

  return result;
};

export const approveProposal = async ({
  proposalId,
  accessToken,
  fallbackBackendUrl,
}: {
  proposalId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postProposalAction({
    path: "/api/proposals/approve",
    proposalId,
    accessToken,
    fallbackBackendUrl,
  });

export const generateProposalPdf = async ({
  proposalId,
  accessToken,
  fallbackBackendUrl,
}: {
  proposalId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postProposalAction({
    path: "/api/proposals/generate-pdf",
    proposalId,
    accessToken,
    fallbackBackendUrl,
  });

export const markProposalSent = async ({
  proposalId,
  accessToken,
  fallbackBackendUrl,
}: {
  proposalId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postProposalAction({
    path: "/api/proposals/mark-sent",
    proposalId,
    accessToken,
    fallbackBackendUrl,
  });

export const sendProposal = async ({
  proposalId,
  accessToken,
  fallbackBackendUrl,
}: {
  proposalId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postProposalAction({
    path: "/api/proposals/send",
    proposalId,
    accessToken,
    fallbackBackendUrl,
  });

export const deleteProposal = async ({
  proposalId,
  accessToken,
  fallbackBackendUrl,
}: {
  proposalId: string;
  accessToken?: string;
  fallbackBackendUrl?: string | null;
}) =>
  postProposalAction({
    path: "/api/proposals/delete",
    proposalId,
    accessToken,
    fallbackBackendUrl,
  });
